import React, { useMemo, useState } from "react";

import { Dropdown } from "@components";

import { useGetAllGradesQuery } from "../../../store/auth/auth.api";

import { RegistrationShell } from "../components/RegistrationShell";

import { useGeniusRegistration } from "../GeniusRegistrationContext";

import { useGeniusGeo } from "../useGeniusGeo";

import { INDIA_ISD } from "../constants";

import { validateMobile, validateName } from "../validations";

import { useGeniusLeadCapture } from "../useGeniusLeadCapture";

import styles from "../GeniusRegistrationModal.module.css";

const sanitizeNameInput = (value) =>
  value
    .replace(/[^a-zA-Z\s]/g, "")
    .replace(/^\s+/, "")
    .replace(/\s{2,}/g, " ");

export const StudentFormStep = () => {
  const { shared, goToSuccess, closeRegistration } = useGeniusRegistration();
  const { states, cities, loadingStates, loadingCities, loadCitiesForState } = useGeniusGeo();
  const { data: gradeList, isLoading: gradesLoading } = useGetAllGradesQuery();
  const { submitStudentRegistrationLead } = useGeniusLeadCapture();
  const [submitting, setSubmitting] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [stateName, setStateName] = useState("");
  const [city, setCity] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const gradeOptions = useMemo(() => {
    if (!gradeList?.length) return [];
    return gradeList.map((g) => ({
      id: String(g.grade_id),
      name: g.grade_name,
      value: String(g.grade_id)
    }));
  }, [gradeList]);

  const cityOptions = cities;

  const handleStateChange = (val) => {
    setStateName(val);
    setCity("");
    loadCitiesForState(val);
  };

  const isFormValid = useMemo(() => {
    return (
      validateName(firstName, { required: true }).valid &&
      validateName(lastName, { required: false }).valid &&
      validateMobile(shared.mobile).valid &&
      Boolean(gradeId) &&
      schoolName.trim().length > 1 &&
      Boolean(stateName) &&
      Boolean(city)
    );
  }, [firstName, lastName, shared.mobile, gradeId, schoolName, stateName, city]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const errors = {};
    const fn = validateName(firstName, { required: true });
    const ln = validateName(lastName, { required: false });
    if (!fn.valid) errors.firstName = fn.message;
    if (!ln.valid) errors.lastName = ln.message;
    if (!gradeId) errors.grade = "Please select your class";
    if (!schoolName.trim()) errors.schoolName = "School name is required";
    if (!stateName) errors.state = "Please select state";
    if (!city) errors.city = "Please select city";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    const gradeName = gradeOptions.find((g) => g.value === gradeId)?.name || gradeId;

    setSubmitting(true);
    try {
      const lsqResult = await submitStudentRegistrationLead({
        phone: shared.mobile,
        firstName: fn.normalized,
        lastName: ln.normalized || "",
        grade: gradeName,
        gradeId,
        schoolName: schoolName.trim(),
        state: stateName,
        city
      });

      if (!lsqResult.success) {
        setFormError("Could not submit your details. Please try again.");
        return;
      }

      goToSuccess();
    } catch {
      setFormError("Could not submit your details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RegistrationShell title="Registration Form" onClose={closeRegistration} wide centerTitle>
      <form className={styles.body} onSubmit={handleSubmit} noValidate>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={`${styles.label} ${styles.required}`}>First name</label>
            <input
              className={`${styles.input} ${fieldErrors.firstName ? styles.inputError : ""}`}
              value={firstName}
              onChange={(e) => setFirstName(sanitizeNameInput(e.target.value))}
              placeholder="Enter first name"
            />
            {fieldErrors.firstName ? <p className={styles.errorText}>{fieldErrors.firstName}</p> : null}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Last name</label>
            <input
              className={`${styles.input} ${fieldErrors.lastName ? styles.inputError : ""}`}
              value={lastName}
              onChange={(e) => setLastName(sanitizeNameInput(e.target.value))}
              placeholder="Enter last name"
            />
            {fieldErrors.lastName ? <p className={styles.errorText}>{fieldErrors.lastName}</p> : null}
          </div>
        </div>

        <div className={styles.field}>
          <Dropdown
            label="Grade"
            placeholder={gradesLoading ? "Loading..." : "Choose class"}
            options={gradeOptions}
            value={gradeId}
            onChange={setGradeId}
            disabled={gradesLoading}
            error={fieldErrors.grade}
            searchable
            size="compact"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Phone number</label>
          <div className={styles.phoneLockedBar}>
            <span>
              {INDIA_ISD} {shared.mobile}
            </span>
          </div>
        </div>

        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Enter your school name</label>
          <input
            className={`${styles.input} ${fieldErrors.schoolName ? styles.inputError : ""}`}
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
            placeholder="Enter school name"
          />
          {fieldErrors.schoolName ? <p className={styles.errorText}>{fieldErrors.schoolName}</p> : null}
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <Dropdown
              label="State"
              placeholder={loadingStates ? "Loading..." : "Select state"}
              options={states}
              value={stateName}
              onChange={handleStateChange}
              disabled={loadingStates}
              error={fieldErrors.state}
              searchable
              size="compact"
            />
          </div>
          <div className={styles.field}>
            <Dropdown
              label="City"
              placeholder={loadingCities ? "Loading..." : "Select city"}
              options={cityOptions}
              value={city}
              onChange={setCity}
              disabled={!stateName || loadingCities}
              error={fieldErrors.city}
              searchable
              size="compact"
            />
          </div>
        </div>

        {formError ? <p className={styles.errorText}>{formError}</p> : null}

        <button
          type="submit"
          className={`${styles.primaryBtn} ${
            !isFormValid || submitting ? styles.primaryBtnDisabled : styles.primaryBtnActive
          }`}
          disabled={!isFormValid || submitting}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </form>
    </RegistrationShell>
  );
};
